import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { institutionApi } from "../../api/institutionsApi";
import { geospatialApi } from "../../api/geospatialApi";
import { livestockActorsApi } from "../../api/workforceApi";
import { pppArrangementsApi } from "../../api/pppArrangementsApi";
import {
  ACTIVITY_REPORT_STATUSES,
  WORKPLAN_ACTIVITY_CATEGORIES,
  WORKPLAN_ACTIVITY_STATUSES,
  WORKPLAN_APPROVAL_STATUSES,
  WORKPLAN_AUTH_LEVELS,
  WORKPLAN_BENEFICIARY_CLASSES,
  WORKPLAN_DEPARTMENTS,
  WORKPLAN_GEO_SCOPES,
  WORKPLAN_PLAN_CATEGORIES,
  WORKPLAN_PLAN_TYPES,
  WORKPLAN_REPORT_TYPES,
  WORKPLAN_STATUSES,
} from "../../api/planningApi";
import { ALL_STATES } from "./mockData/PlanningData";
import {
  collectTextValues,
  injectChoiceOption,
  mergeChoiceLists,
  optionsFromRows,
  optionsFromValues,
} from "./planningOptionUtils";

function normalizeMessage(error, fallback) {
  return error?.response?.data?.message ?? error?.message ?? fallback;
}

function toOptionItem(value, label = value) {
  const text = String(value ?? "").trim();
  if (!text) return null;
  return {
    value: text,
    label: String(label ?? text).trim() || text,
  };
}

function mergeTextOptions(liveValues, fallbackValues = []) {
  return mergeChoiceLists(
    optionsFromValues(liveValues),
    optionsFromValues(fallbackValues)
  );
}

export function usePlanningReferenceOptions({
  workplans = [],
  activities = [],
  reports = [],
  currentUserUuid = "",
  currentUserLabel = "",
  currentInstitutionUuid = "",
  currentInstitutionLabel = "",
} = {}) {
  const [stateOptions, setStateOptions] = useState([]);
  const [institutionOptions, setInstitutionOptions] = useState([]);
  const [actorOptions, setActorOptions] = useState([]);
  const [pppOptions, setPppOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const lgaCacheRef = useRef(new Map());

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setError("");

      // Promise.allSettled (not Promise.all) so a single source failing —
      // e.g. the PPP Arrangement Registry not being available yet — doesn't
      // wipe out options that loaded fine, like institutions or actors.
      const [statesResult, institutionsResult, actorsResult, pppResult] = await Promise.allSettled([
        geospatialApi.states(),
        institutionApi.list({ per_page: 200 }),
        livestockActorsApi.listAll({ per_page: 200 }),
        pppArrangementsApi.listAll({ per_page: 500 }),
      ]);

      if (!active) return;

      const failures = [];

      if (statesResult.status === "fulfilled") {
        const liveStates = Array.isArray(statesResult.value) ? statesResult.value : [];
        setStateOptions(mergeTextOptions(liveStates, ALL_STATES));
      } else {
        setStateOptions(optionsFromValues(ALL_STATES));
        failures.push(normalizeMessage(statesResult.reason, "states"));
      }

      if (institutionsResult.status === "fulfilled") {
        const liveInstitutions = Array.isArray(institutionsResult.value) ? institutionsResult.value : [];
        setInstitutionOptions(
          optionsFromRows(
            liveInstitutions,
            (row) => row.name,
            (row) => row.uuid ?? row.id
          )
        );
      } else {
        setInstitutionOptions([]);
        failures.push(normalizeMessage(institutionsResult.reason, "institutions"));
      }

      if (actorsResult.status === "fulfilled") {
        const liveActors = Array.isArray(actorsResult.value?.items) ? actorsResult.value.items : [];
        setActorOptions(
          optionsFromRows(
            liveActors,
            (row) => row.fullName || row.full_name,
            (row) => row.uuid ?? row.id
          )
        );
      } else {
        setActorOptions([]);
        failures.push(normalizeMessage(actorsResult.reason, "actors"));
      }

      if (pppResult.status === "fulfilled") {
        const livePpp = Array.isArray(pppResult.value?.items) ? pppResult.value.items : [];
        setPppOptions(
          optionsFromRows(
            livePpp,
            (row) => row.pppArrangementId || row.partnerOrgName || row.uuid,
            (row) => row.uuid ?? row.id
          )
        );
      } else {
        setPppOptions([]);
        failures.push(normalizeMessage(pppResult.reason, "PPP arrangements"));
      }

      setError(failures.length ? `Failed to load: ${failures.join(", ")}.` : "");
      if (active) setLoading(false);
    };

    load();

    return () => {
      active = false;
    };
  }, []);

  const loadLgas = useCallback(async (state) => {
    const key = String(state ?? "").trim();
    if (!key) return [];

    if (lgaCacheRef.current.has(key)) {
      return lgaCacheRef.current.get(key);
    }

    try {
      const liveLgas = await geospatialApi.lgas(key);
      const options = optionsFromValues(liveLgas);
      lgaCacheRef.current.set(key, options);
      return options;
    } catch (err) {
      const message = normalizeMessage(err, `Failed to load LGAs for ${key}.`);
      setError(message);
      return [];
    }
  }, []);

  const currentInstitutionOption = useMemo(() => {
    if (!currentInstitutionUuid) return null;
    return toOptionItem(currentInstitutionUuid, currentInstitutionLabel || currentInstitutionUuid);
  }, [currentInstitutionUuid, currentInstitutionLabel]);

  const currentUserOption = useMemo(() => {
    if (!currentUserUuid) return null;
    return toOptionItem(currentUserUuid, currentUserLabel || currentUserUuid);
  }, [currentUserUuid, currentUserLabel]);

  const injectedInstitutionOptions = useMemo(
    () => injectChoiceOption(institutionOptions, currentInstitutionOption),
    [institutionOptions, currentInstitutionOption]
  );

  const injectedActorOptions = useMemo(
    () => injectChoiceOption(actorOptions, currentUserOption),
    [actorOptions, currentUserOption]
  );

  const workplanOptions = useMemo(
    () => optionsFromRows(
      workplans,
      (row) => {
        const title = String(row?.title ?? "").trim();
        const period = String(row?.period ?? row?.year ?? "").trim();
        if (!title) return "";
        return period ? `${title} (${period})` : title;
      },
      (row) => row?.uuid ?? row?.id
    ),
    [workplans]
  );

  // DCIC-12's parent_activity_id must only offer "approved activities" —
  // an activity has no approval status of its own (only Planned/In
  // Progress/Completed/Delayed/Cancelled), so this filters to activities
  // whose parent workplan has cleared approval (Approved/Active/Completed).
  const approvedWorkplanUuids = useMemo(() => {
    const approved = new Set(['approved', 'active', 'completed']);
    return new Set(
      workplans
        .filter((row) => approved.has(String(row?.status ?? '').trim().toLowerCase()))
        .map((row) => row?.uuid ?? row?.id)
        .filter(Boolean)
    );
  }, [workplans]);

  const activityOptions = useMemo(
    () => optionsFromRows(
      activities.filter((row) => approvedWorkplanUuids.has(row?.workplanUuid ?? row?.workplan_uuid)),
      (row) => {
        const title = String(row?.title ?? "").trim();
        const workplan = String(row?.workplan ?? "").trim();
        if (!title) return "";
        return workplan ? `${title} (${workplan})` : title;
      },
      (row) => row?.uuid ?? row?.id
    ),
    [activities, approvedWorkplanUuids]
  );

  const planTypeOptions = useMemo(
    () => mergeTextOptions(
      collectTextValues(workplans, [
        (row) => row?.planType,
        (row) => row?.raw?.plan_type,
      ]),
      WORKPLAN_PLAN_TYPES
    ),
    [workplans]
  );

  const planCategoryOptions = useMemo(
    () => mergeTextOptions(
      collectTextValues(workplans, [
        (row) => row?.planCategory,
        (row) => row?.raw?.plan_category,
      ]),
      WORKPLAN_PLAN_CATEGORIES
    ),
    [workplans]
  );

  const departmentOptions = useMemo(
    () => mergeTextOptions(
      collectTextValues(workplans, [
        (row) => row?.department,
        (row) => row?.raw?.department,
        (row) => row?.raw?.department_name,
      ]),
      WORKPLAN_DEPARTMENTS
    ),
    [workplans]
  );

  const workplanStatusOptions = useMemo(
    () => mergeTextOptions(
      collectTextValues(workplans, [
        (row) => row?.status,
        (row) => row?.raw?.workplan_status,
      ]),
      WORKPLAN_STATUSES
    ),
    [workplans]
  );

  const approvalStatusOptions = useMemo(
    () => mergeTextOptions(
      collectTextValues(workplans, [
        (row) => row?.approvalStatus,
        (row) => row?.raw?.approval_status,
      ]),
      WORKPLAN_APPROVAL_STATUSES
    ),
    [workplans]
  );

  const authorityLevelOptions = useMemo(
    () => mergeTextOptions(
      collectTextValues(workplans, [
        (row) => row?.approvingAuthority,
        (row) => row?.raw?.approving_authority_level,
      ]),
      WORKPLAN_AUTH_LEVELS
    ),
    [workplans]
  );

  const geoScopeOptions = useMemo(
    () => mergeTextOptions(
      collectTextValues(workplans, [
        (row) => row?.geographicScope,
        (row) => row?.raw?.geographic_scope,
      ]),
      WORKPLAN_GEO_SCOPES
    ),
    [workplans]
  );

  const linkedProgrammeOptions = useMemo(
    () => optionsFromValues(
      collectTextValues(workplans, [
        (row) => row?.linkedProgramme,
        (row) => row?.raw?.linked_programme,
      ])
    ),
    [workplans]
  );

  const activityCategoryOptions = useMemo(
    () => mergeTextOptions(
      collectTextValues(activities, [
        (row) => row?.category,
        (row) => row?.raw?.category,
      ]),
      WORKPLAN_ACTIVITY_CATEGORIES
    ),
    [activities]
  );

  const beneficiaryClassOptions = useMemo(
    () => mergeTextOptions(
      collectTextValues(activities, [
        (row) => row?.beneficiaryClass,
        (row) => row?.raw?.beneficiary_class,
      ]),
      WORKPLAN_BENEFICIARY_CLASSES
    ),
    [activities]
  );

  const activityStatusOptions = useMemo(
    () => mergeTextOptions(
      collectTextValues(activities, [
        (row) => row?.status,
        (row) => row?.activityStatus,
        (row) => row?.raw?.activity_status,
      ]),
      WORKPLAN_ACTIVITY_STATUSES
    ),
    [activities]
  );

  const reportStatusOptions = useMemo(
    () => mergeTextOptions(
      collectTextValues(reports, [
        (row) => row?.executionStatus,
        (row) => row?.raw?.execution_status,
        (row) => row?.status,
      ]),
      ACTIVITY_REPORT_STATUSES
    ),
    [reports]
  );

  const reportTypeOptions = useMemo(
    () => mergeTextOptions(
      collectTextValues(reports, [
        (row) => row?.activityType,
        (row) => row?.raw?.activity_type,
      ]),
      WORKPLAN_REPORT_TYPES
    ),
    [reports]
  );

  return {
    loading,
    error,
    stateOptions,
    institutionOptions: injectedInstitutionOptions,
    actorOptions: injectedActorOptions,
    pppOptions,
    workplanOptions,
    activityOptions,
    planTypeOptions,
    planCategoryOptions,
    departmentOptions,
    workplanStatusOptions,
    approvalStatusOptions,
    authorityLevelOptions,
    geoScopeOptions,
    linkedProgrammeOptions,
    activityCategoryOptions,
    beneficiaryClassOptions,
    activityStatusOptions,
    reportStatusOptions,
    reportTypeOptions,
    loadLgas,
  };
}
